import React, { useState, useEffect, useRef } from 'react';
import IntroductionAudio from '../IntroductionAudio';
import aiStudentsImage from '../../../asset/images/home/ai_students.png';
import api from '../../../services/api';
import './VirtualClassPage.css';

const VirtualClassPage = ({ page, lesson, onNext, onCompletionChange }) => {
  const [currentStep, setCurrentStep] = useState(0); 
  const [currentFlashcardIndex, setCurrentFlashcardIndex] = useState(0); 
  const [selectedFlashcard, setSelectedFlashcard] = useState(null);
  const [isStarted, setIsStarted] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [studentResponses, setStudentResponses] = useState({}); 
  const [studentWritings, setStudentWritings] = useState({}); 
  const [showStudentResponses, setShowStudentResponses] = useState(false);
  const [showStudentWritings, setShowStudentWritings] = useState(false);
  const [completedFlashcardsInStep, setCompletedFlashcardsInStep] = useState(new Set()); 
  const [passedFlashcardsInStep, setPassedFlashcardsInStep] = useState(new Set()); 
  const [attemptedFlashcardsInStep, setAttemptedFlashcardsInStep] = useState(new Set()); 
  const [flashcardResult, setFlashcardResult] = useState({}); 
  const [hearSelectedFlashcards, setHearSelectedFlashcards] = useState(new Set()); 
  const [flashcardAttempts, setFlashcardAttempts] = useState({}); 
  const [isProcessing, setIsProcessing] = useState(false); 
  const [isCorrect, setIsCorrect] = useState(null); 
  const [studentResponseAudio, setStudentResponseAudio] = useState(null); 
  const [confusionAudio, setConfusionAudio] = useState(null); 
  const [isPlayingAudio, setIsPlayingAudio] = useState(false); 
  const [isPlayingReminder, setIsPlayingReminder] = useState(false);
  const [showConfusion, setShowConfusion] = useState(false); 
  const [showBoardWriting, setShowBoardWriting] = useState(false); 
  const [shouldStopAnimations, setShouldStopAnimations] = useState(false); 
  const [boardResult, setBoardResult] = useState(null); 
  const [stepResults, setStepResults] = useState({}); 
  const [showStepResult, setShowStepResult] = useState(false); 
  const [currentStepAttempt, setCurrentStepAttempt] = useState(1); 
  const [playedReminderFlashcards, setPlayedReminderFlashcards] = useState(new Set()); 
  const recordingTimerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const isRecordingRef = useRef(false);
  const studentAudioRef = useRef(null);
  const confusionAudioRef = useRef(null);
  const userRecordingRef = useRef(null);
  const reminderAudioRef = useRef(null);
  const animationTimeoutRef = useRef(null); 

  const flashcards = page.flashcards || [];
  const students = page.students || [];
  const currentFlashcard = flashcards[currentFlashcardIndex];

  
  const enabledStepTypes = page.config?.enabledSteps || ['see', 'hear', 'do'];
  
  
  const allSteps = page.steps || [];
  const enabledSteps = allSteps.filter(step => 
    step.type === 'intro' || enabledStepTypes.includes(step.type)
  );
  
  
  const getCurrentStepType = () => {
    if (currentStep === 0) return 'intro';
    const stepIndex = currentStep - 1; 
    const enabledNonIntroSteps = enabledSteps.filter(s => s.type !== 'intro');
    if (stepIndex >= 0 && stepIndex < enabledNonIntroSteps.length) {
      return enabledNonIntroSteps[stepIndex].type;
    }
    return null;
  };
  
  const isStepType = (type) => {
    return getCurrentStepType() === type;
  };
  
  const getMaxStepIndex = () => {
    return enabledSteps.filter(s => s.type !== 'intro').length;
  };

  useEffect(() => {
    if (isRecording) {
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 9) {
            handleSubmitRecording();
            return 9;
          }
          return prev + 1;
        });
      }, 1000);
    } else {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }

    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, [isRecording]);

  const handleStart = () => {
    setIsStarted(true);
    setCurrentStep(1); 
    setCurrentFlashcardIndex(0); 
    setSelectedFlashcard(null); 
    setCompletedFlashcardsInStep(new Set());
    setPassedFlashcardsInStep(new Set()); 
    setAttemptedFlashcardsInStep(new Set()); 
    setHearSelectedFlashcards(new Set());
    setFlashcardAttempts({}); 
    setFlashcardResult({});
    setStudentResponses({});
    setStudentWritings({});
    setShowStudentResponses(false);
    setShowStudentWritings(false); 
    
    if (onCompletionChange) {
      onCompletionChange(false);
    }
  };

  const handleFlashcardClick = (flashcard, index) => {
    const stepType = getCurrentStepType();
    if (stepType === 'see' || stepType === 'hear' || stepType === 'do') {
      const currentFlashcardId = flashcards[currentFlashcardIndex]?.id;
      const isCompleted = completedFlashcardsInStep.has(currentFlashcardId);
      const attempts = flashcardAttempts[currentFlashcardId] || 0;
      const hasTwoAttempts = attempts >= 2;
      
      const canClick = index === currentFlashcardIndex || 
                      (index === currentFlashcardIndex + 1 && (isCompleted || hasTwoAttempts));
      
      if (canClick) {
     
        if (index !== currentFlashcardIndex && stepType === 'do') {
          setStudentWritings({});
          setShowStudentWritings(false);
          setShowBoardWriting(false);
          setBoardResult(null);
          setIsPlayingAudio(false);
          setShouldStopAnimations(true);
          setShowConfusion(false);
        }
        
        setSelectedFlashcard(flashcard);
        setCurrentFlashcardIndex(index);
        if (stepType === 'hear') {
          setHearSelectedFlashcards(prev => new Set([...prev, flashcard.id]));
        }
      }
    }
  };

  const calculateStepResult = (step, additionalPassedFlashcard = null) => {
    const totalFlashcards = flashcards.length;
    let passedFlashcards = Array.from(passedFlashcardsInStep).length;
    
    if (additionalPassedFlashcard && !passedFlashcardsInStep.has(additionalPassedFlashcard)) {
      passedFlashcards += 1;
    }
    
    const percentage = totalFlashcards > 0 ? Math.round((passedFlashcards / totalFlashcards) * 100) : 0;
    
    const passThreshold = totalFlashcards === 3 ? 66 : (totalFlashcards === 5 ? 80 : 80);
    const passed = percentage >= passThreshold;
    
    return { percentage, passed, totalFlashcards, passedFlashcards };
  };

  const checkCompletion = (step, completedFlashcards, hasStudentWritings) => {
    const allFlashcardsDoneInDo = step === 3 && completedFlashcards.size === flashcards.length;
    
    const isCompleted = allFlashcardsDoneInDo;
    
    if (onCompletionChange) {
      onCompletionChange(isCompleted);
    }
    
    return isCompleted;
  };

  const handleNextFlashcard = () => {
    const allFlashcardsDone = flashcards.every(flashcard => 
      completedFlashcardsInStep.has(flashcard.id)
    );

    // Clear board state when moving to next flashcard
    setStudentWritings({});
    setShowStudentWritings(false);
    setShowBoardWriting(false);
    setBoardResult(null);
    setIsPlayingAudio(false);
    setShouldStopAnimations(true);

    if (!allFlashcardsDone && currentFlashcardIndex < flashcards.length - 1) {
      const nextIndex = currentFlashcardIndex + 1;
      const stepType = getCurrentStepType();
      setCurrentFlashcardIndex(nextIndex);
      if (stepType === 'see') {
        setSelectedFlashcard(null);
      } else {
        setSelectedFlashcard(flashcards[nextIndex]);
      }
      setIsRecording(false);
      setRecordingTime(0);
      setShowStudentResponses(false);
      setShowConfusion(false);
      setIsCorrect(null);
      // Clean up previous user recording
      if (userRecordingRef.current) {
        URL.revokeObjectURL(userRecordingRef.current);
        userRecordingRef.current = null;
      }
      if (stepType === 'do') {
        setStudentWritings({});
        setShowStudentWritings(false);
        setShowBoardWriting(false);
      }
    } else if (allFlashcardsDone || currentFlashcardIndex >= flashcards.length - 1) {
      if (!showStepResult) {
        setTimeout(() => {
          const stepResult = calculateStepResult(currentStep);
          
          setStepResults(prev => ({
            ...prev,
            [currentStep]: {
              ...stepResult,
              attempt: currentStepAttempt
            }
          }));
          
          // Show step result modal after a delay
          setTimeout(() => {
            setShowStepResult(true);
          }, 2000); // 2 second delay
          
          if (stepResult.passed || currentStepAttempt >= 2) {
            
          } else {
          }
        }, 150); 
      }
    }
  };

  const handleMoveToNextStep = () => {
    const maxStepIndex = getMaxStepIndex();
    if (currentStep < maxStepIndex) {
      setShowStepResult(false);
      const currentStepType = getCurrentStepType();
      setCurrentStep(currentStep + 1);
      setCurrentFlashcardIndex(0); 
      if (currentStepType === 'see') {
        setSelectedFlashcard(null);
      } else {
        setSelectedFlashcard(flashcards[0]);
      }
      setCompletedFlashcardsInStep(new Set()); 
      setPassedFlashcardsInStep(new Set()); 
      setAttemptedFlashcardsInStep(new Set()); 
      setFlashcardAttempts({}); 
      setFlashcardResult({}); 
      setIsRecording(false);
      setRecordingTime(0);
      setShowStudentResponses(false);
      setShowConfusion(false);
      setIsCorrect(null);
      setShouldStopAnimations(false); 
      setCurrentStepAttempt(1);
      // Reset played reminder flashcards when moving to next step
      setPlayedReminderFlashcards(new Set());
      // Clean up user recording
      if (userRecordingRef.current) {
        URL.revokeObjectURL(userRecordingRef.current);
        userRecordingRef.current = null;
      } 
      
      if (currentStepType === 'see') {
        setStudentResponses({});
        setShowStudentResponses(false);
      } else if (currentStepType === 'hear') {
        setStudentResponses({});
        setShowStudentResponses(false);
        setStudentWritings({});
        setShowStudentWritings(false);
        setShowBoardWriting(false);
      }
    } else {
      setShowStepResult(false);
      if (onNext) {
        onNext();
      }
    }
  };

  const handleRetryStep = () => {
    setShowStepResult(false);
    setCurrentFlashcardIndex(0);
    setCompletedFlashcardsInStep(new Set());
    setPassedFlashcardsInStep(new Set()); 
    setAttemptedFlashcardsInStep(new Set());
    setFlashcardAttempts({});
    setFlashcardResult({}); 
    setIsRecording(false);
    setRecordingTime(0);
    setShowStudentResponses(false);
    setShowConfusion(false);
    setIsCorrect(null);
    setShouldStopAnimations(false);
    setCurrentStepAttempt(2);
    // Reset played reminder flashcards when retrying step
    setPlayedReminderFlashcards(new Set());
    // Clean up user recording
    if (userRecordingRef.current) {
      URL.revokeObjectURL(userRecordingRef.current);
      userRecordingRef.current = null;
    } 
    
    const stepType = getCurrentStepType();
    if (stepType === 'see') {
      setSelectedFlashcard(null);
    } else {
      setSelectedFlashcard(flashcards[0]);
    }
    
    if (stepType === 'see') {
      setStudentResponses({});
    } else if (stepType === 'hear') {
      setStudentResponses({});
    } else if (stepType === 'do') {
      setStudentWritings({});
      setShowStudentWritings(false);
      setShowBoardWriting(false);
    }
  };

  const floatTo16BitPCM = (output, offset, input) => {
    for (let i = 0; i < input.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, input[i]));
      output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }
  };

  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  const encodeWAV = (samples, sampleRate, numChannels) => {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);
    
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + samples.length * 2, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); 
    view.setUint16(20, 1, true); 
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numChannels * 2, true); 
    view.setUint16(32, numChannels * 2, true); 
    view.setUint16(34, 16, true); 
    writeString(view, 36, 'data');
    view.setUint32(40, samples.length * 2, true);
    
    floatTo16BitPCM(view, 44, samples);
    
    return buffer;
  };

  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) {
          throw new Error('Web Audio API not supported in this browser');
        }
        
        const audioContext = new AudioContext({ sampleRate: 16000 }); 
        audioContextRef.current = audioContext;
        
        const source = audioContext.createMediaStreamSource(stream);
        const processor = audioContext.createScriptProcessor(4096, 1, 1);
        
        audioChunksRef.current = [];
        
        processor.onaudioprocess = (e) => {
          if (!isRecordingRef.current) return;
          const inputData = e.inputBuffer.getChannelData(0);
          const samples = new Float32Array(inputData.length);
          samples.set(inputData);
          audioChunksRef.current.push(samples);
        };
        
        source.connect(processor);
        processor.connect(audioContext.destination);
        
        mediaRecorderRef.current = { processor, source, audioContext };
        isRecordingRef.current = true;
        
        setIsRecording(true);
        setRecordingTime(0);
        setIsCorrect(null);
        setShowConfusion(false);
        
      } catch (webAudioError) {
        
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];
        
        mediaRecorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };
        
        mediaRecorder.start(100);
        isRecordingRef.current = true;
        setIsRecording(true);
        setRecordingTime(0);
        setIsCorrect(null);
        setShowConfusion(false);
      }
    } catch (error) {
      alert('Unable to access microphone. Please check your permissions.');
    }
  };

  const handleSubmitRecording = async () => {
    isRecordingRef.current = false;
    setIsRecording(false);
    
    let audioBlob;
    let isWavFormat = false;
    
    const isWebAudio = mediaRecorderRef.current && mediaRecorderRef.current.processor;
    
    if (isWebAudio) {
      const { processor, source } = mediaRecorderRef.current;
      if (processor) {
        processor.disconnect();
      }
      if (source) {
        source.disconnect();
      }
      
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }

      await new Promise(resolve => setTimeout(resolve, 200));

      if (audioChunksRef.current.length === 0) {
        alert('No audio was recorded. Please try again.');
        return;
      }

      const totalLength = audioChunksRef.current.reduce((sum, chunk) => sum + (chunk.length || 0), 0);
      if (totalLength === 0) {
        alert('No audio was recorded. Please try again.');
        return;
      }
      
      const combinedSamples = new Float32Array(totalLength);
      let offset = 0;
      for (const chunk of audioChunksRef.current) {
        if (chunk && chunk.length > 0) {
          combinedSamples.set(chunk, offset);
          offset += chunk.length;
        }
      }

      const sampleRate = audioContextRef.current?.sampleRate || 16000;
      const wavBuffer = encodeWAV(combinedSamples, sampleRate, 1); 
      audioBlob = new Blob([wavBuffer], { type: 'audio/wav' });
      isWavFormat = true;
      
    } else {
      
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        const stopPromise = new Promise((resolve) => {
          if (mediaRecorderRef.current) {
            const originalOnStop = mediaRecorderRef.current.onstop;
            mediaRecorderRef.current.onstop = () => {
              if (originalOnStop) {
                originalOnStop();
              }
              setTimeout(() => resolve(), 100);
            };
          } else {
            resolve();
          }
        });

        if (mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.requestData();
        }
        mediaRecorderRef.current.stop();
        await stopPromise;
      }
      
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }

      if (audioChunksRef.current.length === 0) {
        alert('No audio was recorded. Please try again.');
        return;
      }

      const webmBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioContext({ sampleRate: 16000 });
        
        const arrayBuffer = await webmBlob.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        
        const samples = audioBuffer.getChannelData(0); 
        const sampleRate = audioBuffer.sampleRate;
        
        const wavBuffer = encodeWAV(samples, sampleRate, 1);
        audioBlob = new Blob([wavBuffer], { type: 'audio/wav' });
        isWavFormat = true;
        
        await audioContext.close();
      } catch (conversionError) {
        audioBlob = webmBlob;
        isWavFormat = false;
      }
    }

    setIsProcessing(true);
    setShowConfusion(false);
    setIsCorrect(null);

    const userRecordingUrl = URL.createObjectURL(audioBlob);
    userRecordingRef.current = userRecordingUrl;

    try {
      const lessonId = lesson?.databaseId || lesson?.id || page.lessonId;
      if (!lessonId) {
        throw new Error('Lesson ID is required');
      }

      if (!selectedFlashcard) {
        throw new Error('No flashcard selected');
      }

      const flashcardId = selectedFlashcard.id;
      const currentAttempt = flashcardAttempts[flashcardId] || 0;
      const attemptNumber = currentAttempt + 1; 

      // Create form data
      const formData = new FormData();
      const filename = isWavFormat ? 'recording.wav' : 'recording.webm';
      formData.append('audio', audioBlob, filename);
      const stepType = getCurrentStepType();
      formData.append('exerciseType', stepType || 'see');
      formData.append('flashcardId', flashcardId);
      formData.append('pageId', page.id);
      formData.append('expectedWord', selectedFlashcard.word);
      formData.append('attemptNumber', attemptNumber.toString());
      

      const response = await api.post(`/lessons/${lessonId}/exercise-submit`, formData);

      const result = response.data.data;
      setIsCorrect(result.passed || result.is_correct);
      
      
      const hasCustomResponse = selectedFlashcard?.customResponse?.[stepType];
      
      const flashcardCustomAudio = selectedFlashcard?.customResponse?.[stepType]?.url || 
                                   selectedFlashcard?.customResponse?.[stepType]?.audio?.url || 
                                   selectedFlashcard?.customResponse?.[stepType]?.audio;
      const backendCustomAudio = (hasCustomResponse && (result.student_response_audio || result.studentResponseAudio)) || null;
      const customResponseAudio = flashcardCustomAudio || backendCustomAudio;
      
      
      if (stepType === 'see' || stepType === 'hear') {
        if (result.passed || result.is_correct) {
          setFlashcardResult(prev => ({ ...prev, [flashcardId]: 'correct' }));
          setTimeout(() => {
            setFlashcardResult(prev => {
              const newResult = { ...prev };
              delete newResult[flashcardId];
              return newResult;
            });
          }, 2000);
          
          
          if (stepType === 'see') {
            
            setShowStudentResponses(true);
            setIsPlayingAudio(true);
            setShouldStopAnimations(false);
            
           
            const customResponseText = selectedFlashcard?.customResponse?.[stepType]?.text;
            const responseText = customResponseText || selectedFlashcard?.word || 'word';
            const responses = {};
            students.forEach((student) => {
              responses[student.id] = responseText;
            });
            setStudentResponses(responses);
          } else {
           
            setIsPlayingAudio(true);
            setShouldStopAnimations(true); 
          }
          
         
         
          const navigateAfterAudio = () => {
            const updatedCompletedSet = new Set([...completedFlashcardsInStep, flashcardId]);
            const allCompleted = updatedCompletedSet.size === flashcards.length;
            
            if (!allCompleted) {
              setTimeout(() => {
                handleNextFlashcard();
              }, 1500); 
            }
          };
          
          // If custom response is defined in JSON, use it; otherwise play user recording
          if (customResponseAudio) {
            playStudentResponseAudio(customResponseAudio, () => {
              setIsPlayingAudio(false);
              setShouldStopAnimations(true);
              if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
                animationTimeoutRef.current = null;
              }
              navigateAfterAudio();
            });
          } else {
            playUserRecording(() => {
              setIsPlayingAudio(false);
              setShouldStopAnimations(true);
              if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
                animationTimeoutRef.current = null;
              }
              navigateAfterAudio();
            });
          }
        } else {
          setFlashcardResult(prev => ({ ...prev, [flashcardId]: 'incorrect' }));
          setTimeout(() => {
            setFlashcardResult(prev => {
              const newResult = { ...prev };
              delete newResult[flashcardId];
              return newResult;
            });
          }, 2000);
          
          setShowConfusion(true);
          setShouldStopAnimations(false); 
          
         
          playUserRecording(() => {
            
            const { reminderAudio, isFlashcardSpecific } = getReminderAudio(selectedFlashcard, currentStep, true);
            
           
            const shouldPlayReminder = reminderAudio && (!isFlashcardSpecific || !playedReminderFlashcards.has(flashcardId));
            
            if (shouldPlayReminder) {
              setIsPlayingReminder(true);
              playReminderAudio(reminderAudio, () => {
                setIsPlayingReminder(false);
              });
              
             
              if (isFlashcardSpecific) {
                setPlayedReminderFlashcards(prev => new Set([...prev, flashcardId]));
              }
            } else {
             
              setShowConfusion(false);
              setShouldStopAnimations(true);
            }
          });
        }
      } else if (stepType === 'do') {
        if (result.passed || result.is_correct) {
          setShowBoardWriting(true);
          setBoardResult('correct');
          setShowStudentWritings(true);
          const writings = {};
          const responseText = selectedFlashcard?.customResponse?.do?.text || selectedFlashcard?.word || 'word';
          students.forEach((student) => {
            writings[student.id] = responseText;
          });
          setStudentWritings(prev => ({ ...prev, ...writings }));
          
          // Don't auto-hide board result - keep it visible until user clicks next
          // setTimeout(() => {
          //   setBoardResult(null);
          // }, 2000);
          
         
          setIsPlayingAudio(true);
          setShouldStopAnimations(false);
          
          // Check if custom response is defined in JSON for DO step
          const hasCustomResponse = selectedFlashcard?.customResponse?.do;
          // Priority: JSON audio/url > backend TTS (if custom text exists) > user recording
          // Handle both structures: customResponse.do.url OR customResponse.do.audio.url
          const flashcardCustomAudio = selectedFlashcard?.customResponse?.do?.url || 
                                       selectedFlashcard?.customResponse?.do?.audio?.url || 
                                       selectedFlashcard?.customResponse?.do?.audio;
          const backendCustomAudio = (hasCustomResponse && (result.student_response_audio || result.studentResponseAudio)) || null;
          const customResponseAudio = flashcardCustomAudio || backendCustomAudio;
          
         
          if (customResponseAudio) {
            playStudentResponseAudio(customResponseAudio, () => {
              setIsPlayingAudio(false);
              setShouldStopAnimations(false); 
              if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
                animationTimeoutRef.current = null;
              }
            });
          } else {
            playUserRecording(() => {
              setIsPlayingAudio(false);
              setShouldStopAnimations(false); 
              if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
                animationTimeoutRef.current = null;
              }
            });
          }
        } else {
          setBoardResult('incorrect');
          setStudentWritings({});
          setShowStudentWritings(false);
          setShowBoardWriting(false);
          setShowConfusion(true);
          
          setTimeout(() => {
            setBoardResult(null);
          }, 2000);
          
          setShouldStopAnimations(false); 
          
         
          playUserRecording(() => {
            
            const { reminderAudio, isFlashcardSpecific } = getReminderAudio(selectedFlashcard, currentStep, true);
            
            const shouldPlayReminder = reminderAudio && (!isFlashcardSpecific || !playedReminderFlashcards.has(flashcardId));
            
            if (shouldPlayReminder) {
              setIsPlayingReminder(true);
              playReminderAudio(reminderAudio, () => {
                setIsPlayingReminder(false);
              });
              
              if (isFlashcardSpecific) {
                setPlayedReminderFlashcards(prev => new Set([...prev, flashcardId]));
              }
            } else {
              setShowConfusion(false);
              setShouldStopAnimations(true);
            }
          });
        }
      }

      const newAttempt = result.attemptNumber || attemptNumber;
      
      setFlashcardAttempts(prev => ({
        ...prev,
        [flashcardId]: newAttempt
      }));

      if (stepType === 'see') {
        setAttemptedFlashcardsInStep(prev => new Set([...prev, flashcardId]));
      }

      const wasPassed = result.passed || result.is_correct;
      
      setPassedFlashcardsInStep(prev => {
        const newPassedSet = wasPassed ? new Set([...prev, flashcardId]) : prev;
        return newPassedSet;
      });
      
      const isDone = wasPassed || (newAttempt >= 2 && !wasPassed);
      
      if (isDone) {
        setCompletedFlashcardsInStep(prev => {
          const newSet = new Set([...prev, flashcardId]);
          const currentStepType = getCurrentStepType();
          if (currentStepType === 'do' && newSet.size === flashcards.length) {
            checkCompletion(currentStep, newSet, Object.keys(studentWritings).length > 0);
          }
          return newSet;
        });
      }

      const shouldMoveToNext = result.shouldMoveToNext !== undefined 
        ? result.shouldMoveToNext 
        : (result.passed || result.is_correct || newAttempt >= 2); 

      if (shouldMoveToNext) {
        if (isDone) {
          const updatedCompletedSet = new Set([...completedFlashcardsInStep, flashcardId]);
          const updatedPassedSet = wasPassed ? new Set([...passedFlashcardsInStep, flashcardId]) : passedFlashcardsInStep;
          
          if (updatedCompletedSet.size === flashcards.length) {
            setTimeout(() => {
              const totalFlashcards = flashcards.length;
              const passedFlashcards = Array.from(updatedPassedSet).length;
              const percentage = totalFlashcards > 0 ? Math.round((passedFlashcards / totalFlashcards) * 100) : 0;
              const passThreshold = totalFlashcards === 3 ? 66 : (totalFlashcards === 5 ? 80 : 80);
              const passed = percentage >= passThreshold;
              
              const stepResult = {
                percentage,
                passed,
                totalFlashcards,
                passedFlashcards
              };
              
              setStepResults(prev => ({
                ...prev,
                [currentStep]: {
                  ...stepResult,
                  attempt: currentStepAttempt
                }
              }));
              
              setShowStepResult(true);
              
              const stepType = getCurrentStepType();
              if (stepType === 'do') {
                checkCompletion(currentStep, updatedCompletedSet, Object.keys(studentWritings).length > 0);
              }
            }, 150);
          }
        }

        
        const updatedCompletedSet = new Set([...completedFlashcardsInStep, flashcardId]);
        const allCompleted = updatedCompletedSet.size === flashcards.length;
        
        if (!allCompleted && !wasPassed) {
         
          let delay = 2000; 
          const stepType = getCurrentStepType();
          
          if (stepType === 'see') {
            delay = 3000;
          } else if (stepType === 'hear') {
            delay = isPlayingAudio ? 5000 : 3000;
          } else if (stepType === 'do') {
            delay = 3000; 
          }

          setTimeout(() => {
            if (stepType === 'do') {
              setStudentWritings({});
              setShowStudentWritings(false);
              setShowBoardWriting(false);
            }
            handleNextFlashcard();
          }, delay);
        }
      } else {
        const stepType = getCurrentStepType();
        if (stepType === 'do') {
          setTimeout(() => {
            setStudentWritings({});
            setShowStudentWritings(false);
            setShowBoardWriting(false);
          }, 2000); 
        }
      }
    } catch (error) {
      setShowConfusion(true);
    } finally {
      setIsProcessing(false);
      audioChunksRef.current = [];
      isRecordingRef.current = false;
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(console.error);
      }
    }
  };

  const playStudentResponseAudio = (audioData) => {
    try {
      if (!audioData) {
        return;
      }

      let audioUrl;
      
      if (typeof audioData === 'string' && (audioData.startsWith('http://') || audioData.startsWith('https://'))) {
        audioUrl = audioData;
      } else if (typeof audioData === 'string' && audioData.length > 0) {
        try {
          const audioBytes = Uint8Array.from(atob(audioData), c => c.charCodeAt(0));
          const audioBlob = new Blob([audioBytes], { type: 'audio/wav' });
          audioUrl = URL.createObjectURL(audioBlob);
        } catch (base64Error) {
          return;
        }
      } else {
        return;
      }
      
      if (studentAudioRef.current) {
        studentAudioRef.current.pause();
        studentAudioRef.current.src = '';
      }
      
      const audio = new Audio(audioUrl);
      studentAudioRef.current = audio;
      setIsPlayingAudio(true);
      
      audio.onended = () => {
        setIsPlayingAudio(false);
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
          animationTimeoutRef.current = null;
        }
        if (audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(audioUrl);
        }
      };
      
      audio.onerror = (error) => {
        console.error('Error playing audio:', error);
        setIsPlayingAudio(false);
        if (audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(audioUrl);
        }
      };
      
      audio.play().catch(error => {
        console.error('Error starting audio playback:', error);
        setIsPlayingAudio(false);
        if (audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(audioUrl);
        }
      });
    } catch (error) {
      console.error('Error playing student response audio:', error);
      setIsPlayingAudio(false);
    }
  };

  const playConfusionAudio = (base64Audio) => {
    try {
      const audioBytes = Uint8Array.from(atob(base64Audio), c => c.charCodeAt(0));
      const audioBlob = new Blob([audioBytes], { type: 'audio/wav' });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      if (confusionAudioRef.current) {
        confusionAudioRef.current.pause();
        confusionAudioRef.current.src = '';
      }
      
      const audio = new Audio(audioUrl);
      confusionAudioRef.current = audio;
      
      audio.onended = () => {
        setShowConfusion(false);
        setShouldStopAnimations(true); 
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
          animationTimeoutRef.current = null;
        }
        URL.revokeObjectURL(audioUrl);
      };
      
      audio.onerror = () => {
        setShowConfusion(false);
        setShouldStopAnimations(true); 
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
          animationTimeoutRef.current = null;
        }
        URL.revokeObjectURL(audioUrl);
      };
      
      audio.play();
    } catch (error) {
    }
  };

  const playUserRecording = async (onEnded) => {
    if (!userRecordingRef.current) {
      if (onEnded) onEnded();
      return;
    }

    try {
      // Use Web Audio API to apply pitch shift for child-like voice
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const response = await fetch(userRecordingRef.current);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Create source
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      
      // Apply pitch shift: increase pitch by ~25% to sound more child-like
      // Using playbackRate to shift pitch (1.25 = +25% pitch, sounds more child-like)
      // Note: This also speeds up playback slightly, but creates a child-like effect
      source.playbackRate.value = 1.25;
      
      // Connect to destination
      source.connect(audioContext.destination);
      
      source.onended = () => {
        // Don't close context immediately, let it finish
        setTimeout(() => {
          if (audioContext.state !== 'closed') {
            audioContext.close().catch(() => {});
          }
        }, 100);
        if (onEnded) onEnded();
      };
      
      source.onerror = () => {
        if (audioContext.state !== 'closed') {
          audioContext.close().catch(() => {});
        }
        if (onEnded) onEnded();
      };
      
      source.start(0);
    } catch (error) {
      console.error('Error playing user recording with voice changer:', error);
      try {
        const audio = new Audio(userRecordingRef.current);
        audio.onended = () => {
          if (onEnded) onEnded();
        };
        audio.onerror = () => {
          if (onEnded) onEnded();
        };
        audio.play().catch(() => {
          if (onEnded) onEnded();
        });
      } catch (fallbackError) {
        console.error('Error in fallback audio playback:', fallbackError);
        if (onEnded) onEnded();
      }
    }
  };

  const getReminderAudio = (flashcard, step, returnInfo = false) => {
    let isFlashcardSpecific = false;
    let reminderAudio = null;
    
    // Convert step number to step type if needed
    let stepType = step;
    if (typeof step === 'number') {
      if (step === 0) stepType = 'intro';
      else {
        const enabledNonIntroSteps = enabledSteps.filter(s => s.type !== 'intro');
        if (step > 0 && step <= enabledNonIntroSteps.length) {
          stepType = enabledNonIntroSteps[step - 1].type;
        } else {
          stepType = null;
        }
      }
    }
    
    // Check for flashcard-specific reminder audio first
    // Structure: reminderAudio.hear or reminderAudio.do
    if (flashcard?.reminderAudio && stepType) {
      if (stepType === 'hear' && flashcard.reminderAudio.hear) {
        reminderAudio = flashcard.reminderAudio.hear;
        isFlashcardSpecific = true;
      } else if (stepType === 'do' && flashcard.reminderAudio.do) {
        reminderAudio = flashcard.reminderAudio.do;
        isFlashcardSpecific = true;
      } else if (stepType === 'see' && flashcard.reminderAudio.see) {
        reminderAudio = flashcard.reminderAudio.see;
        isFlashcardSpecific = true;
      }
    }
    
    // If no flashcard-specific reminder, check for step-specific reminder audio (for SEE, HEAR, and DO step general reminders)
    if (!reminderAudio && stepType) {
      const stepConfig = enabledSteps.find(s => s.type === stepType);
      
      if (stepConfig?.reminderAudio && (stepType === 'see' || stepType === 'hear' || stepType === 'do')) {
        // Use step-level reminder for SEE, HEAR, and DO steps
        reminderAudio = stepConfig.reminderAudio;
        isFlashcardSpecific = false;
      }
    }
    
    if (returnInfo) {
      return { reminderAudio, isFlashcardSpecific };
    }
    
    return reminderAudio;
  };

  const playReminderAudio = (reminderAudio, onEnded) => {
    try {
      let audioUrl;
      
      if (typeof reminderAudio === 'string') {
        // If it's a URL string
        if (reminderAudio.startsWith('http://') || reminderAudio.startsWith('https://')) {
          audioUrl = reminderAudio;
        } else {
          if (onEnded) onEnded();
          return;
        }
      } else if (reminderAudio?.url) {
        audioUrl = reminderAudio.url;
      } else {
        if (onEnded) onEnded();
        return;
      }
      
      if (reminderAudioRef.current) {
        reminderAudioRef.current.pause();
        reminderAudioRef.current.src = '';
      }
      
      const audio = new Audio(audioUrl);
      reminderAudioRef.current = audio;
      
      audio.onended = () => {
        setShowConfusion(false);
        setShouldStopAnimations(true); 
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
          animationTimeoutRef.current = null;
        }
        if (onEnded) onEnded();
      };
      
      audio.onerror = () => {
        setShowConfusion(false);
        setShouldStopAnimations(true); 
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
          animationTimeoutRef.current = null;
        }
        if (onEnded) onEnded();
      };
      
      audio.play().catch(error => {
        console.error('Error starting reminder audio playback:', error);
        setShowConfusion(false);
        setShouldStopAnimations(true);
        if (onEnded) onEnded();
      });
    } catch (error) {
      console.error('Error playing reminder audio:', error);
      setShowConfusion(false);
      setShouldStopAnimations(true);
      if (onEnded) onEnded();
    }
  };

  const playReminderAudioFromIcon = (flashcard, step) => {
    const reminderAudio = getReminderAudio(flashcard, step);
    if (!reminderAudio) return;
    
    setIsPlayingReminder(true);
    playReminderAudio(reminderAudio, () => {
      setIsPlayingReminder(false);
    });
  };

  const getReminderAudioForStep = (flashcard, step) => {
    let stepType = step;
    if (typeof step === 'number') {
      if (step === 0) stepType = 'intro';
      else {
        const enabledNonIntroSteps = enabledSteps.filter(s => s.type !== 'intro');
        if (step > 0 && step <= enabledNonIntroSteps.length) {
          stepType = enabledNonIntroSteps[step - 1].type;
        } else {
          stepType = null;
        }
      }
    }
    
    if (flashcard?.reminderAudio && stepType) {
      if (stepType === 'hear' && flashcard.reminderAudio.hear) {
        return flashcard.reminderAudio.hear;
      } else if (stepType === 'do' && flashcard.reminderAudio.do) {
        return flashcard.reminderAudio.do;
      } else if (stepType === 'see' && flashcard.reminderAudio.see) {
        return flashcard.reminderAudio.see;
      }
    }
    return null;
  };

  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (studentAudioRef.current) {
        studentAudioRef.current.pause();
      }
      if (confusionAudioRef.current) {
        confusionAudioRef.current.pause();
      }
      if (reminderAudioRef.current) {
        reminderAudioRef.current.pause();
      }
      if (userRecordingRef.current) {
        URL.revokeObjectURL(userRecordingRef.current);
      }
    };
  }, []);

  const getCurrentStepAudio = () => {
    if (currentStep === 0) {
      return page.steps?.find(s => s.type === 'intro')?.introductionAudio || page.introductionAudio;
    }
    return null;
  };

  const getCurrentStepInstruction = () => {
    const stepType = getCurrentStepType();
    if (stepType === 'intro') {
      return page.steps?.find(s => s.type === 'intro')?.instruction;
    } else if (stepType === 'see') {
      return page.steps?.find(s => s.type === 'see')?.instruction;
    } else if (stepType === 'hear') {
      return page.steps?.find(s => s.type === 'hear')?.instruction;
    } else if (stepType === 'do') {
      return page.steps?.find(s => s.type === 'do')?.instruction;
    }
    return null;
  };

  const renderStepContent = () => {
    if (currentStep === 0) {
      const introStepAudio = page.steps?.find(s => s.type === 'intro')?.introductionAudio || page.introductionAudio;
      
      return (
        <div className="text-center">
          {introStepAudio && (
            <div className="mb-6">
              <IntroductionAudio
                audio={introStepAudio}
                autoPlay={introStepAudio.autoPlay}
              />
            </div>
          )}
          
          <div className="mb-6">
            <div className="flex justify-center gap-4 mb-4">
              <div className="bg-blue-500 text-white px-6 py-2 rounded-full font-bold text-lg">SEE</div>
              <div className="bg-blue-500 text-white px-6 py-2 rounded-full font-bold text-lg">HEAR</div>
              <div className="bg-blue-500 text-white px-6 py-2 rounded-full font-bold text-lg">DO</div>
            </div>
            {getCurrentStepInstruction() && (
              <p className="text-gray-700 text-lg max-w-2xl mx-auto">{getCurrentStepInstruction()}</p>
            )}
          </div>
          {!isStarted && (
            <button
              onClick={handleStart}
              className="px-8 py-3 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg"
            >
              Start
            </button>
          )}
        </div>
      );
    }

    if (isStepType('see')) {
      const seeStepAudio = enabledSteps.find(s => s.type === 'see')?.introductionAudio;
      
      return (
        <div>
          <div className="mb-2 flex items-center gap-2">
            <div className="bg-blue-500 text-white px-4 py-1.5 rounded-full font-bold text-sm">
              SEE
            </div>
            {getCurrentStepInstruction() && (
              <p className="text-gray-700 text-sm">{getCurrentStepInstruction()}</p>
            )}
          </div>

          {seeStepAudio && (
            <div className="mb-2 scale-90 origin-top-left">
              <IntroductionAudio
                audio={seeStepAudio}
                autoPlay={seeStepAudio.autoPlay}
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-2 mb-2">
                <h3 className="font-bold text-gray-800 mb-2 text-sm">Flashcards</h3>
                <div className="space-y-1.5">
                  {flashcards.map((flashcard, index) => {
                    const isCurrent = index === currentFlashcardIndex;
                    const isCompleted = completedFlashcardsInStep.has(flashcard.id);
                    const isAttempted = attemptedFlashcardsInStep.has(flashcard.id);
                    const canClick = isCurrent || (index === currentFlashcardIndex + 1 && isAttempted);
                    const isDisabled = !canClick && index > currentFlashcardIndex;
                    const isSelected = selectedFlashcard?.id === flashcard.id;
                    
                    const result = flashcardResult[flashcard.id];
                    return (
                      <button
                        key={flashcard.id}
                        onClick={() => handleFlashcardClick(flashcard, index)}
                        className={`w-full p-2 text-left border-2 rounded-lg transition-all ${
                          result === 'correct'
                            ? 'flashcard-correct'
                            : result === 'incorrect'
                            ? 'flashcard-incorrect'
                            : isSelected
                            ? 'border-red-500 bg-red-50'
                            : isCompleted
                            ? 'border-green-500 bg-green-50 opacity-60'
                            : isDisabled
                            ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                            : 'border-gray-300 bg-white hover:bg-gray-50'
                        }`}
                        disabled={isDisabled}
                      >
                        <span className="text-lg font-bold">{flashcard.word}</span>
                        {isCurrent && !isSelected && (
                          <span className="ml-2 text-sm text-blue-600 font-semibold">← Click to select</span>
                        )}
                        {isSelected && (
                          <span className="ml-2 text-sm text-red-600 font-semibold">← Selected</span>
                        )}
                        {isCompleted && !isSelected && (
                          <span className="ml-2 text-sm text-green-600 font-semibold">✓ Done</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div>
              <div className="bg-white border border-gray-300 rounded-lg p-2 mb-2">
                <h3 className="font-bold text-gray-800 mb-2 text-sm">Students</h3>
                
                <div className="relative mb-2 flashcard-container">
                  <img
                    src={aiStudentsImage}
                    alt="Virtual Students"
                    className="w-full h-auto max-h-64 object-cover rounded-lg shadow-sm"
                  />
                  <div 
                    className="absolute bottom-2 left-1/2"
                    style={{ 
                      minWidth: '160px',
                      minHeight: '100px',
                      perspective: '1000px',
                      perspectiveOrigin: 'center center'
                    }}
                  >
                    {selectedFlashcard ? (
                      <div 
                        className="flashcard-3d flashcard-flipped"
                        style={{
                          width: '100%',
                          height: '100%',
                          transform: 'translateX(-50%) translateZ(50px) rotateY(180deg) rotateX(-5deg) scale(1.05)',
                          transformStyle: 'preserve-3d',
                          animation: 'cardFlipIn 0.6s ease-out'
                        }}
                      >
                        <div 
                          className="flashcard-back"
                          style={{
                            background: 'white',
                            border: '4px solid #9ca3af',
                            borderRadius: '8px',
                            padding: '24px',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '220px',
                            minHeight: '140px',
                            backfaceVisibility: 'hidden',
                            WebkitBackfaceVisibility: 'hidden'
                          }}
                        >
                          <p 
                            className="text-4xl font-bold"
                            style={{ 
                              transform: 'scaleX(-1)',
                              color: '#1f2937',
                              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.1)'
                            }}
                          >
                            {selectedFlashcard.word}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="absolute bottom-0 left-1/2 bg-white border-4 border-gray-300 rounded-lg p-6 text-center shadow-lg"
                        style={{ 
                          transform: 'translateX(-50%)',
                          minWidth: '220px',
                          minHeight: '140px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0.7
                        }}
                      >
                        <p className="text-gray-400 text-sm">Click a word to show it here</p>
                      </div>
                    )}
                  </div>
                </div>

                {selectedFlashcard && (
                  <div className="mt-2 text-center">
                    {isProcessing ? (
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mb-2"></div>
                        <p className="text-xs font-medium text-gray-700">Processing...</p>
                      </div>
                    ) : !isRecording ? (
                      <button
                        onClick={handleStartRecording}
                        disabled={isProcessing || isPlayingReminder}
                        className="w-20 h-20 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center mx-auto transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                        </svg>
                      </button>
                    ) : (
                      <div>
                        <button
                          onClick={handleSubmitRecording}
                          className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-1 animate-pulse shadow-lg"
                        >
                          <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 012 0v4a1 1 0 11-2 0V7zm4 0a1 1 0 012 0v4a1 1 0 11-2 0V7z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <button
                          onClick={handleSubmitRecording}
                          disabled={recordingTime < 3}
                          className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                            recordingTime < 3
                              ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                              : 'bg-green-500 text-white hover:bg-green-600'
                          }`}
                        >
                          {recordingTime < 3 ? `Wait ${3 - recordingTime}s...` : 'Submit'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {!isRecording && !isProcessing && selectedFlashcard && 
           (completedFlashcardsInStep.has(selectedFlashcard.id) || attemptedFlashcardsInStep.has(selectedFlashcard.id)) && (
            <div className="mt-2 flex justify-end">
              {currentFlashcardIndex < flashcards.length - 1 ? (
                <button
                  onClick={handleNextFlashcard}
                  className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Next Word →
                </button>
              ) : (
                <button
                  onClick={handleNextFlashcard}
                  className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Continue to HEAR →
                </button>
              )}
            </div>
          )}

          <div className="mt-4 text-center text-sm text-gray-600">
            Flashcard {currentFlashcardIndex + 1} of {flashcards.length} in SEE step
            {completedFlashcardsInStep.size > 0 && (
              <span className="ml-2 text-green-600">({completedFlashcardsInStep.size} completed)</span>
            )}
          </div>
        </div>
      );
    }

    if (isStepType('hear')) {
      const hearStepAudio = enabledSteps.find(s => s.type === 'hear')?.introductionAudio;
      
      return (
        <div>
          <div className="mb-2 flex items-center gap-2">
            <div className="bg-blue-500 text-white px-4 py-1.5 rounded-full font-bold text-sm">
              HEAR
            </div>
            {getCurrentStepInstruction() && (
              <p className="text-gray-700 text-sm">{getCurrentStepInstruction()}</p>
            )}
          </div>

          {hearStepAudio && (
            <div className="mb-2 scale-90 origin-top-left">
              <IntroductionAudio
                audio={hearStepAudio}
                autoPlay={hearStepAudio.autoPlay}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-2 mb-2">
                <h3 className="font-bold text-gray-800 mb-2 text-sm">Flashcards (Tap to show for kids)</h3>
                <div className="space-y-1.5">
                  {flashcards.map((flashcard, index) => {
                    const isCurrent = index === currentFlashcardIndex;
                    const isCompleted = completedFlashcardsInStep.has(flashcard.id);
                    const isAttempted = attemptedFlashcardsInStep.has(flashcard.id);
                    const currentFlashcardId = flashcards[currentFlashcardIndex]?.id;
                    const currentIsCompleted = completedFlashcardsInStep.has(currentFlashcardId);
                    const currentAttempts = flashcardAttempts[currentFlashcardId] || 0;
                    const currentHasTwoAttempts = currentAttempts >= 2;
                    // Allow clicking next flashcard if current is completed OR has 2 attempts
                    const canClick = isCurrent || (index === currentFlashcardIndex + 1 && (currentIsCompleted || currentHasTwoAttempts));
                    const isDisabled = !canClick && index > currentFlashcardIndex;
                    const isSelected = selectedFlashcard?.id === flashcard.id;
                    const wasShown = hearSelectedFlashcards.has(flashcard.id);
                    
                    const result = flashcardResult[flashcard.id];
                    return (
                      <button
                        key={flashcard.id}
                        onClick={() => handleFlashcardClick(flashcard, index)}
                        className={`w-full p-2 text-left border-2 rounded-lg transition-all ${
                          result === 'correct'
                            ? 'flashcard-correct'
                            : result === 'incorrect'
                            ? 'flashcard-incorrect'
                            : isSelected
                            ? 'border-red-500 bg-red-50'
                            : wasShown || isCompleted
                            ? 'border-green-500 bg-green-50 opacity-60'
                            : isDisabled
                            ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                            : 'border-gray-300 bg-white hover:bg-gray-50'
                        }`}
                        disabled={isCompleted || isDisabled}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-bold">{flashcard.word}</span>
                          {getReminderAudioForStep(flashcard, currentStep) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                playReminderAudioFromIcon(flashcard, currentStep);
                              }}
                              className="ml-2 p-1.5 hover:bg-gray-200 rounded-full transition-colors"
                              title="Play reminder audio"
                            >
                              <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20" strokeWidth="1.5">
                                <path d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.793L4.383 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.383l4-3.617a1 1 0 011.617.793zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" />
                              </svg>
                            </button>
                          )}
                        </div>
                        {isSelected && (
                          <span className="ml-2 text-sm text-red-600 font-semibold">← Showing</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {!isRecording && !showStudentResponses && completedFlashcardsInStep.size === flashcards.length && (
                <div className="mt-2 flex justify-end">
                  <button
                    onClick={handleNextFlashcard}
                    className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Continue to DO →
                  </button>
                </div>
              )}
            </div>

            <div>
              <div className="bg-white border border-gray-300 rounded-lg p-2 mb-2">
                <h3 className="font-bold text-gray-800 mb-2 text-sm">Students</h3>
                <div className={`relative mb-2 ${isPlayingAudio && !shouldStopAnimations ? 'students-speaking' : ''} ${showConfusion && !shouldStopAnimations ? 'students-confused' : ''}`}>
                  <img
                    src={aiStudentsImage}
                    alt="Virtual Students"
                    className={`w-full h-auto max-h-64 object-cover rounded-lg shadow-sm ${isPlayingAudio ? 'audio-playing' : ''}`}
                  />
                  {selectedFlashcard && (
                    <div className="absolute bottom-2 left-1/2 bg-white border-2 border-gray-400 rounded-lg p-2 text-center shadow-2xl" style={{ transform: 'translateX(-50%) rotateY(-20deg) scaleX(-1)', transformStyle: 'preserve-3d', backfaceVisibility: 'visible' }}>
                      <p className="text-xl font-bold mb-1" style={{ transform: 'scaleX(-1)' }}>{selectedFlashcard.word}</p>
                    </div>
                  )}
                </div>
                
                {showConfusion && !isProcessing && (
                  <div className="mt-2 p-2 bg-red-50 border-2 border-red-300 rounded-lg text-center">
                    <p className="text-sm font-bold text-red-600">Huh...?</p>
                    <p className="text-xs text-gray-700 mt-1">Try again!</p>
                  </div>
                )}

                {selectedFlashcard && (
                  <div className="mt-2 text-center">
                    {isProcessing ? (
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mb-2"></div>
                        <p className="text-xs font-medium text-gray-700">Processing...</p>
                      </div>
                    ) : !isRecording ? (
                      <button
                        onClick={handleStartRecording}
                        disabled={isProcessing || isPlayingReminder}
                        className="w-20 h-20 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center mx-auto transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                        </svg>
                      </button>
                    ) : (
                      <div>
                        <button
                          onClick={handleSubmitRecording}
                          className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-1 animate-pulse shadow-lg"
                        >
                          <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 012 0v4a1 1 0 11-2 0V7zm4 0a1 1 0 012 0v4a1 1 0 11-2 0V7z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <button
                          onClick={handleSubmitRecording}
                          disabled={recordingTime < 3}
                          className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                            recordingTime < 3
                              ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                              : 'bg-green-500 text-white hover:bg-green-600'
                          }`}
                        >
                          {recordingTime < 3 ? `Wait ${3 - recordingTime}s...` : 'Submit'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="mt-4 text-center text-sm text-gray-600">
            Flashcard {currentFlashcardIndex + 1} of {flashcards.length} in HEAR step
            {completedFlashcardsInStep.size > 0 && (
              <span className="ml-2 text-green-600">({completedFlashcardsInStep.size} completed)</span>
            )}
          </div>
        </div>
      );
    }

    if (isStepType('do')) {
      const doStepAudio = enabledSteps.find(s => s.type === 'do')?.introductionAudio;
      
      return (
        <div>
          <div className="mb-2 flex items-center gap-2">
            <div className="bg-blue-500 text-white px-4 py-1.5 rounded-full font-bold text-sm">
              DO
            </div>
            {getCurrentStepInstruction() && (
              <p className="text-gray-700 text-sm">{getCurrentStepInstruction()}</p>
            )}
          </div>

          {doStepAudio && (
            <div className="mb-2 scale-90 origin-top-left">
              <IntroductionAudio
                audio={doStepAudio}
                autoPlay={doStepAudio.autoPlay}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-2 mb-2">
                <h3 className="font-bold text-gray-800 mb-2 text-sm">Flashcards</h3>
                <div className="space-y-1.5">
                  {flashcards.map((flashcard, index) => {
                    const isCurrent = index === currentFlashcardIndex;
                    const isCompleted = completedFlashcardsInStep.has(flashcard.id);
                    const isAttempted = attemptedFlashcardsInStep.has(flashcard.id);
                    const canClick = isCurrent || (index === currentFlashcardIndex + 1 && isAttempted);
                    const isDisabled = !canClick && index > currentFlashcardIndex;
                    const isSelected = selectedFlashcard?.id === flashcard.id;
                    
                    return (
                      <button
                        key={flashcard.id}
                        onClick={() => handleFlashcardClick(flashcard, index)}
                        className={`w-full p-2 text-left border-2 rounded-lg transition-all ${
                          isSelected
                            ? 'border-red-500 bg-red-50'
                            : isCompleted
                            ? 'border-green-500 bg-green-50 opacity-60'
                            : isDisabled
                            ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                            : 'border-gray-300 bg-white hover:bg-gray-50'
                        }`}
                        disabled={isCompleted || isDisabled}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-bold">{flashcard.word}</span>
                          {getReminderAudioForStep(flashcard, currentStep) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                playReminderAudioFromIcon(flashcard, currentStep);
                              }}
                              className="ml-2 p-1.5 hover:bg-gray-200 rounded-full transition-colors"
                              title="Play reminder audio"
                            >
                              <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20" strokeWidth="1.5">
                                <path d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.793L4.383 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.383l4-3.617a1 1 0 011.617.793zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" />
                              </svg>
                            </button>
                          )}
                        </div>
                        {isSelected && (
                          <span className="ml-2 text-xs text-red-600 font-semibold">← Current</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {!isRecording && !isProcessing && isStepType('do') && completedFlashcardsInStep.size === flashcards.length && (
                <div className="mt-2 p-2 bg-green-50 border-2 border-green-300 rounded-lg text-center">
                  <p className="text-sm font-bold text-green-700 mb-1">✓ All flashcards completed!</p>
                  <p className="text-xs text-gray-700 mb-2">You've finished all words in the DO step.</p>
                  {onNext && (
                    <button
                      onClick={() => {
                        console.log('Continue to Next Page clicked');
                        if (onNext) {
                          onNext();
                        }
                      }}
                      className="px-4 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold cursor-pointer"
                    >
                      Continue to Next Page
                    </button>
                  )}
                </div>
              )}
              
            </div>

            <div>
              <div className="bg-white border border-gray-300 rounded-lg p-2">
                <h3 className="font-bold text-gray-800 mb-2 text-sm">Students</h3>
                
                <div className={`relative mb-2 ${showBoardWriting ? 'board-writing' : ''} ${showConfusion && !shouldStopAnimations ? 'students-confused' : ''}`}>
                  <img
                    src={aiStudentsImage}
                    alt="Virtual Students"
                    className="w-full h-auto max-h-64 object-cover rounded-lg shadow-sm"
                  />
                  
                  {showConfusion && !isProcessing && (
                    <div className="absolute top-2 left-1/2 transform -translate-x-1/2 p-2 bg-red-50 border-2 border-red-300 rounded-lg text-center z-10">
                      <p className="text-sm font-bold text-red-600">Huh...?</p>
                      <p className="text-xs text-gray-700 mt-1">Try again!</p>
                    </div>
                  )}
                  
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-11/12">
                    <div className="grid grid-cols-5 gap-2">
                      {students.map((student) => (
                        <div
                          key={student.id}
                          className={`student-board ${showBoardWriting && studentWritings[student.id] ? 'board-holding' : ''} ${boardResult === 'correct' ? 'student-board-correct' : ''} ${boardResult === 'incorrect' ? 'student-board-incorrect' : ''}`}
                        >
                          {studentWritings[student.id] ? (
                            (() => {
                              
                              const renderWordWithMarkings = (word, flashcard) => {
                                if (!flashcard || !flashcard.vowels || !Array.isArray(flashcard.vowels) || flashcard.vowels.length === 0) {
                                  return <div className="student-board-text">{word}</div>;
                                }
                                
         
                                const syllableDiv = flashcard.syllableDivision;
                                const syllableDivisions = Array.isArray(syllableDiv) ? syllableDiv : (syllableDiv !== undefined && syllableDiv !== null ? [syllableDiv] : []);
                                
                                if (syllableDivisions.length === 0) {
                                  return <div className="student-board-text">{word}</div>;
                                }
                                
                                const vowels = flashcard.vowels || [];
                                const letters = word.split('');
                                
                                return (
                                  <div className="student-board-text relative inline-flex items-end flex-wrap" style={{ paddingBottom: '8px' }}>
                                    {letters.map((letter, index) => {
                                      const isVowel = vowels.includes(index);
                                     
                                      const showDivisionLineAfter = syllableDivisions.some(divPos => divPos === index + 1) && index < letters.length - 1;
                                      
                                      return (
                                        <span key={index} className="relative inline-block">
                                          {letter}
                                          {isVowel && (
                                            <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 z-10" style={{ bottom: '-6px' }}>
                                              <span className="inline-block w-2 h-2 bg-red-600 rounded-full"></span>
                                            </span>
                                          )}
                                          {showDivisionLineAfter && (
                                            <span className="absolute top-0 right-0 h-full w-0.5 bg-gray-800 transform translate-x-full"></span>
                                          )}
                                        </span>
                                      );
                                    })}
                                  </div>
                                );
                              };
                              
                             
                              const syllableDiv = selectedFlashcard?.syllableDivision;
                              const syllableDivArray = Array.isArray(syllableDiv) ? syllableDiv : (syllableDiv !== undefined && syllableDiv !== null ? [syllableDiv] : []);
                              
                              const shouldShowMarkings = boardResult === 'correct' && 
                                selectedFlashcard?.vowels && 
                                Array.isArray(selectedFlashcard.vowels) && 
                                selectedFlashcard.vowels.length > 0 &&
                                syllableDivArray.length > 0;
                              
                              return shouldShowMarkings
                                ? renderWordWithMarkings(studentWritings[student.id], selectedFlashcard)
                                : <div className="student-board-text">{studentWritings[student.id]}</div>;
                            })()
                          ) : (
                            <div className="text-gray-400 text-xs">...</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {selectedFlashcard && (
                  <div className="mt-2 text-center">
                    {isProcessing ? (
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mb-2"></div>
                        <p className="text-xs font-medium text-gray-700">Processing...</p>
                      </div>
                    ) : !isRecording ? (
                      <button
                        onClick={handleStartRecording}
                        disabled={isProcessing || isPlayingReminder}
                        className="w-20 h-20 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center mx-auto transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                        </svg>
                      </button>
                    ) : (
                      <div>
                        <button
                          onClick={handleSubmitRecording}
                          className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-1 animate-pulse shadow-lg"
                        >
                          <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 012 0v4a1 1 0 11-2 0V7zm4 0a1 1 0 012 0v4a1 1 0 11-2 0V7z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <button
                          onClick={handleSubmitRecording}
                          disabled={recordingTime < 3}
                          className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                            recordingTime < 3
                              ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                              : 'bg-green-500 text-white hover:bg-green-600'
                          }`}
                        >
                          {recordingTime < 3 ? `Wait ${3 - recordingTime}s...` : 'Submit'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="mt-4 text-center text-sm text-gray-600">
            Flashcard {currentFlashcardIndex + 1} of {flashcards.length} in DO step
            {completedFlashcardsInStep.size > 0 && (
              <span className="ml-2 text-green-600">({completedFlashcardsInStep.size} completed)</span>
            )}
          </div>
        </div>
      );
    }

    return null;
  };



  useEffect(() => {
    if (isStepType('do') && completedFlashcardsInStep.size === flashcards.length) {
      checkCompletion(currentStep, completedFlashcardsInStep, Object.keys(studentWritings).length > 0);
    }
  }, [currentStep, completedFlashcardsInStep, flashcards.length, studentWritings]);

  useEffect(() => {
  }, [page]);

  return (
    <div className="relative">
      
      {isRecording && (
        <div className="fixed top-20 -right-2 z-50 bg-red-600 text-white px-8 py-3 rounded-lg shadow-lg min-w-[120px]">
          <div className="text-3xl font-bold text-center">
            {9 - recordingTime}s
          </div>
        </div>
      )}

      {page.title && (
        <h2 className="text-lg font-bold mb-2 text-gray-800">{page.title}</h2>
      )}

      {renderStepContent()}
      
      {showStepResult && (() => {
        const stepResult = stepResults[currentStep];
        if (!stepResult) return null;
        
        const stepNames = { 1: 'SEE', 2: 'HEAR', 3: 'DO' };
        const stepName = stepNames[currentStep] || 'Step';
        
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-xl">
              <h2 className="text-2xl font-bold mb-4 text-center text-gray-800">
                {stepName} Step Results
              </h2>
              
              <div className="text-center mb-6">
                <div className={`text-6xl font-bold mb-2 ${stepResult.passed ? 'text-green-600' : 'text-red-600'}`}>
                  {stepResult.percentage}%
                </div>
                <p className="text-lg text-gray-700">
                  {stepResult.passedFlashcards} out of {stepResult.totalFlashcards} flashcards completed
                </p>
                <p className={`text-xl font-semibold mt-2 ${stepResult.passed ? 'text-green-600' : 'text-red-600'}`}>
                  {stepResult.passed ? '✓ Passed' : '✗ Not Passed'}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Pass threshold: {stepResult.totalFlashcards === 3 ? '66%' : (stepResult.totalFlashcards === 5 ? '80%' : '80%')}
                </p>
              </div>
              
              <div className="flex gap-4 justify-center">
                {stepResult.passed || stepResult.attempt >= 2 ? (
                  <button
                    onClick={handleMoveToNextStep}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                  >
                    {currentStep < 3 ? 'Continue to Next Step' : 'Finish'}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleRetryStep}
                      className="px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-semibold"
                    >
                      Try Again
                    </button>
                    <button
                      className="px-6 py-3 bg-gray-400 text-white rounded-lg cursor-not-allowed opacity-50"
                      disabled
                    >
                      Continue (Disabled)
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {isStarted && (
        <div className="mt-6 text-center">
          <div className="flex justify-center gap-2 mb-2">
            <div className={`px-4 py-1 rounded-full text-sm font-medium ${
              currentStep >= 1 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              SEE
            </div>
            <div className={`px-4 py-1 rounded-full text-sm font-medium ${
              currentStep >= 2 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              HEAR
            </div>
            <div className={`px-4 py-1 rounded-full text-sm font-medium ${
              currentStep >= 3 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              DO
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VirtualClassPage;
